import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { autocompleteQuizsetTitles, type QuizsetSearchFilter } from '@/api';
import { usePublicTitleAutocompletePayload } from '@/module/public/hooks/usePublicTitleAutocompletePayload';

const TITLE_AUTOCOMPLETE_MIN_QUERY_LENGTH = 2;
const TITLE_AUTOCOMPLETE_LIMIT = 5;
const TITLE_AUTOCOMPLETE_DEBOUNCE_MS = 400;

export const useQuizsetTitleAutocomplete = (
  query: string,
  enabled: boolean,
) => {
  const trimmedQuery = query.trim();
  const [debouncedQuery, setDebouncedQuery] = useState(trimmedQuery);
  const {
    categoryFilters,
    subjectFilter,
    enabled: isFilterContextReady,
  } = usePublicTitleAutocompletePayload();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(trimmedQuery);
    }, TITLE_AUTOCOMPLETE_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [trimmedQuery]);

  const requestCategoryFilters = useMemo<QuizsetSearchFilter>(() => {
    return {
      criteria: categoryFilters,
      ...subjectFilter,
    };
  }, [categoryFilters, subjectFilter]);

  const filterContextKey = useMemo(
    () => JSON.stringify(requestCategoryFilters),
    [requestCategoryFilters],
  );

  const canSearchCurrentQuery =
    enabled &&
    isFilterContextReady &&
    trimmedQuery.length >= TITLE_AUTOCOMPLETE_MIN_QUERY_LENGTH;
  const canFetch =
    enabled &&
    isFilterContextReady &&
    debouncedQuery.length >= TITLE_AUTOCOMPLETE_MIN_QUERY_LENGTH;

  const queryResult = useQuery({
    queryKey: ['quizsets/title-autocomplete', debouncedQuery, filterContextKey],
    queryFn: ({ signal }) =>
      autocompleteQuizsetTitles(
        {
          keyword: debouncedQuery,
          limit: TITLE_AUTOCOMPLETE_LIMIT,
          categoryFilters: requestCategoryFilters,
        },
        undefined,
        signal,
      ),
    enabled: canFetch,
    staleTime: 1000 * 60 * 3,
    placeholderData: previousData => previousData,
    retry: false,
    throwOnError: false,
    meta: { ignoreError: true },
  });

  return {
    ...queryResult,
    suggestions:
      canSearchCurrentQuery && !queryResult.isError
        ? queryResult.data?.data ?? []
        : [],
    isAutocompleteEnabled: canSearchCurrentQuery,
  };
};
